import { useState } from 'react';
import {
  Alert,
  Checkbox,
  FormControlLabel,
  FormGroup,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import AdminButton from './AdminButton';
import { useSnackbar } from '../../../contexts/SnackBarContext';
import { formatPrice } from '../../../utils/priceUtils';
import CustomModal from './modals/CustomModal';

const CODE_PATTERN = /^[A-Z0-9-]{3,32}$/;
const DATE_FORMAT = 'YYYY.MM.DD.';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 'min(480px, calc(100vw - 32px))',
  maxHeight: 'calc(100vh - 32px)',
  overflowY: 'auto',
  boxSizing: 'border-box',
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  p: { xs: 3, sm: 4 },
};

const emptyForm = () => ({
  code: '',
  discountType: 'PERCENT',
  discountValue: '',
  validFrom: dayjs().startOf('day'),
  validTo: dayjs().add(1, 'month').startOf('day'),
  isForAllSupplies: true,
  supplyIds: [],
});

const formOf = (coupon) => ({
  code: coupon.code,
  discountType: coupon.discountType,
  discountValue: String(coupon.discountValue),
  validFrom: dayjs(coupon.validFrom),
  validTo: dayjs(coupon.validTo),
  isForAllSupplies: coupon.isForAllSupplies,
  supplyIds: coupon.supplyIds ?? [],
});

// The backend refuses the same things (DEC-0033); checking here first says which field is wrong.
const validationError = (form) => {
  if (!CODE_PATTERN.test(form.code)) {
    return 'A kód 3–32 karakter legyen: betű, szám vagy kötőjel.';
  }
  const value = Number(form.discountValue);
  if (!Number.isInteger(value) || value < 1) return 'Add meg a kedvezmény mértékét egész számmal.';
  if (form.discountType === 'PERCENT' && value > 100) return 'A kedvezmény legfeljebb 100% lehet.';
  if (!form.validFrom?.isValid() || !form.validTo?.isValid()) return 'Add meg az érvényesség kezdetét és végét.';
  if (form.validTo.isBefore(form.validFrom, 'day')) return 'Az érvényesség vége nem lehet a kezdete előtt.';
  if (!form.isForAllSupplies && form.supplyIds.length === 0) return 'Válassz legalább egy szolgáltatást.';
  return null;
};

const requestOf = (form) => ({
  code: form.code,
  discountType: form.discountType,
  discountValue: Number(form.discountValue),
  validFrom: form.validFrom.format('YYYY-MM-DD'),
  validTo: form.validTo.format('YYYY-MM-DD'),
  isForAllSupplies: form.isForAllSupplies,
  supplyIds: form.isForAllSupplies ? [] : form.supplyIds,
});

/**
 * Creating a coupon, or changing one: the same form. Every field can be changed at any time, also
 * after bookings were made with it; they keep the price they were confirmed at, which the form
 * says when there are any. A supply without a price ("Egyeztetés alapján") cannot be chosen: there
 * is nothing to take a discount off. Rendered only while open, so every opening starts from the
 * coupon as it is, or from an empty form.
 */
const CouponFormModal = ({ coupon, supplies, onClose, onSave, isSaving }) => {
  const [form, setForm] = useState(() => (coupon ? formOf(coupon) : emptyForm()));
  const showSnackbar = useSnackbar();

  const set = (field) => (value) => setForm((previous) => ({ ...previous, [field]: value }));

  const toggleSupply = (supplyId) => setForm((previous) => ({
    ...previous,
    supplyIds: previous.supplyIds.includes(supplyId)
      ? previous.supplyIds.filter((id) => id !== supplyId)
      : [...previous.supplyIds, supplyId],
  }));

  const handleSave = () => {
    const error = validationError(form);
    if (error) {
      showSnackbar(error, 'error');
      return;
    }
    onSave(requestOf(form));
  };

  return (
    <CustomModal open onClose={onClose} title={coupon ? 'Kupon módosítása' : 'Új kupon'} style={modalStyle}>
      {coupon?.redemptionCount > 0 && (
        <Alert severity="info" sx={{ '& .MuiAlert-message': { fontSize: 14 } }}>
          Már {coupon.redemptionCount} foglalás készült ezzel a kuponnal. Ezek a foglaláskori áron
          maradnak; a módosítás csak a későbbi beváltásokra vonatkozik.
        </Alert>
      )}

      <TextField
        label="Kód"
        value={form.code}
        onChange={(event) => set('code')(event.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ''))}
        helperText="Ezt írja be a vendég. Betű, szám és kötőjel, ékezet nélkül."
        inputProps={{ maxLength: 32, 'data-testid': 'coupon-code' }}
        fullWidth
      />

      <Stack direction="row" spacing={2} alignItems="flex-start">
        <ToggleButtonGroup
          exclusive
          value={form.discountType}
          onChange={(event, value) => value && set('discountType')(value)}
          size="small"
          // MUI capitalises toggle labels; "FT" is not how forints are written.
          sx={{ height: 56, '& .MuiToggleButton-root': { textTransform: 'none' } }}
        >
          <ToggleButton value="PERCENT" data-testid="coupon-type-percent" sx={{ px: 2 }}>%</ToggleButton>
          <ToggleButton value="FIXED" data-testid="coupon-type-fixed" sx={{ px: 2 }}>Ft</ToggleButton>
        </ToggleButtonGroup>
        <TextField
          label="Kedvezmény"
          type="number"
          value={form.discountValue}
          onChange={(event) => set('discountValue')(event.target.value)}
          inputProps={{ min: 1, max: form.discountType === 'PERCENT' ? 100 : undefined, 'data-testid': 'coupon-value' }}
          InputProps={{
            endAdornment: <InputAdornment position="end">{form.discountType === 'PERCENT' ? '%' : 'Ft'}</InputAdornment>,
          }}
          fullWidth
        />
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <DatePicker
          label="Érvényes ettől"
          value={form.validFrom}
          onChange={set('validFrom')}
          format={DATE_FORMAT}
          slotProps={{ textField: { fullWidth: true, inputProps: { 'data-testid': 'coupon-valid-from' } } }}
        />
        <DatePicker
          label="Érvényes eddig"
          value={form.validTo}
          onChange={set('validTo')}
          minDate={form.validFrom?.isValid() ? form.validFrom : undefined}
          format={DATE_FORMAT}
          slotProps={{ textField: { fullWidth: true, inputProps: { 'data-testid': 'coupon-valid-to' } } }}
        />
      </Stack>
      <Typography variant="body2" sx={{ color: 'text.secondary', mt: -1.5 }}>
        Mindkét nap beleszámít. A beváltás napja számít, nem a foglalt időponté.
      </Typography>

      <Stack spacing={1}>
        <FormControlLabel
          control={(
            <Switch
              checked={form.isForAllSupplies}
              onChange={(event) => set('isForAllSupplies')(event.target.checked)}
              inputProps={{ 'data-testid': 'coupon-all-supplies' }}
            />
          )}
          label="Minden szolgáltatásra"
        />
        {!form.isForAllSupplies && (
          <FormGroup sx={{ pl: 1 }}>
            {supplies.map((supply) => {
              const hasPrice = supply.price !== null && supply.price !== undefined;
              return (
                <FormControlLabel
                  key={supply.id}
                  control={(
                    <Checkbox
                      checked={form.supplyIds.includes(supply.id)}
                      onChange={() => toggleSupply(supply.id)}
                      disabled={!hasPrice}
                      inputProps={{ 'data-testid': `coupon-supply-${supply.id}` }}
                    />
                  )}
                  label={(
                    <Typography variant="body2">
                      {supply.name}
                      <Typography component="span" variant="body2" sx={{ color: 'text.secondary' }}>
                        {` · ${formatPrice(supply.price)}`}
                      </Typography>
                    </Typography>
                  )}
                />
              );
            })}
          </FormGroup>
        )}
      </Stack>

      <AdminButton text="Mentés" onClick={handleSave} isLoading={isSaving} />
    </CustomModal>
  );
};

export default CouponFormModal;
